<script setup>
import { computed, inject } from 'vue';
import { flowThreadContextKey } from './flowThreadContext';
import NodeBadge from '../ui/NodeBadge.vue';
import NodeIconButton from '../ui/NodeIconButton.vue';
import NodeInlineField from '../ui/NodeInlineField.vue';
import NodeQuickInsertMenu from '../ui/NodeQuickInsertMenu.vue';
import NodeTextareaField from '../ui/NodeTextareaField.vue';

const props = defineProps({
  node: {
    type: Object,
    required: true,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  listRows: {
    type: Array,
    default: () => [],
  },
});

const context = inject(flowThreadContextKey);
const hasBodyValue = computed(() => Boolean(props.node.config?.body?.trim?.()));
const hasButtonTextValue = computed(() =>
  Boolean(props.node.config?.buttonText?.trim?.())
);
</script>

<template>
  <div class="bg-[#dcf8c6] px-3 py-2">
    <NodeTextareaField
      v-if="isSelected"
      :model-value="node.config?.body || ''"
      min-height="2.75rem"
      max-height="5rem"
      variant="bubbleCompact"
      :placeholder="context.uiCopy.chooseList"
      @update:model-value="context.updateNode({ body: $event })"
    />
    <div
      v-else
      class="text-[12px] leading-5"
      :class="hasBodyValue ? 'text-n-slate-12' : 'text-n-slate-9'"
    >
      {{ node.config?.body || context.uiCopy.chooseList }}
    </div>
    <div
      class="mt-2 flex h-[30px] w-full items-center justify-between rounded-xl border border-black/5 bg-white px-3 text-[12px] font-medium text-n-slate-12"
    >
      <NodeInlineField
        v-if="isSelected"
        :model-value="node.config?.buttonText || ''"
        :placeholder="context.uiCopy.viewList"
        variant="plain"
        @update:model-value="context.updateNode({ buttonText: $event })"
      />
      <span
        v-else
        :class="hasButtonTextValue ? 'text-n-slate-12' : 'text-n-slate-9'"
      >
        {{ node.config?.buttonText || context.uiCopy.viewList }}
      </span>
      <i class="i-lucide-chevron-left rtl:rotate-180 text-sm text-n-slate-10" />
    </div>
    <div class="mt-1.5 flex items-center justify-end gap-1 text-[10px]">
      <span class="text-n-slate-10">{{ context.uiCopy.timestamp }}</span>
    </div>
  </div>

  <div
    v-if="listRows.length"
    class="border-t border-black/5 bg-white px-2 py-1.5"
  >
    <div
      v-for="branch in listRows"
      :key="branch.id"
      class="relative mb-1.5 flex items-center gap-1.5 last:mb-0"
    >
      <div
        class="flex min-w-0 flex-1 items-center justify-between rounded-xl border border-black/5 bg-white px-2.5 py-2 transition focus-within:border-[#25d366]/55 focus-within:shadow-[0_0_0_2px_rgba(37,211,102,0.12)]"
      >
        <div class="min-w-0 flex-1">
          <NodeInlineField
            v-if="isSelected"
            :model-value="branch.item?.node?.config?.title || ''"
            :placeholder="context.uiCopy.listRowTitlePlaceholder"
            variant="surfaceMd"
            @update:model-value="
              context.emitUpdateNode({
                nodeId: branch.item?.node?.id,
                patch: { title: $event },
              })
            "
          />
          <div v-else class="truncate text-[12px] font-medium text-n-slate-12">
            {{ branch.item?.node?.config?.title }}
          </div>
          <div
            v-if="branch.item?.node?.config?.description"
            class="mt-0.5 truncate text-[11px] text-n-slate-11"
          >
            {{ branch.item.node.config.description }}
          </div>
        </div>
        <div class="ms-2 flex items-center gap-1">
          <i
            class="i-lucide-chevron-left rtl:rotate-180 text-sm text-n-slate-10"
          />
        </div>
      </div>
      <NodeIconButton
        icon="i-lucide-plus"
        variant="positiveSoft"
        size="sm"
        :data-quick-insert-trigger="`row_${branch.item?.node?.id}`"
        :title="context.uiCopy.addAfterRow"
        @click.stop="
          context.toggleQuickInsertMenu(`row_${branch.item?.node?.id}`)
        "
      />
      <NodeBadge
        v-if="
          branch.item?.next?.node &&
          context.quickInsertMenuTarget.value !== `row_${branch.item?.node?.id}`
        "
        :label="context.branchContinuationSummary(branch)"
        class="max-w-[8.5rem] cursor-pointer transition hover:bg-[#25d366]/14"
        variant="successOutline"
        @click.stop="context.selectNode(branch.item?.next?.node?.id)"
      />
      <NodeQuickInsertMenu
        v-if="
          context.quickInsertMenuTarget.value === `row_${branch.item?.node?.id}`
        "
        :target-id="`row_${branch.item?.node?.id}`"
        :options="context.branchInsertItems.value"
        @select="context.insertBranchNode($event, branch.item?.node?.id || '')"
      />
    </div>
  </div>
</template>
